import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { api } from '../api/client.js';
import {
  Badge, Breadcrumbs, Card, ConfidenceMeter, Empty, ErrorNotice, Loading, Tile, fmt, fmtDate,
} from '../components/ui.jsx';

const BAND_COLOR = {
  high: 'var(--ok)', medium: 'var(--warn)', low: 'var(--danger)', none: 'var(--border-strong)',
};

export default function DashboardPage() {
  const overview = useQuery({ queryKey: ['overview'], queryFn: () => api.overview().then((r) => r.data) });
  const coverage = useQuery({ queryKey: ['insurerCoverage'], queryFn: () => api.insurerCoverage().then((r) => r.data) });

  if (overview.isLoading) return <div className="page"><Loading rows={8} /></div>;
  if (overview.error) return <div className="page"><ErrorNotice error={overview.error} /></div>;

  const d = overview.data;
  const bands = d.mappings.bands;
  const bandData = [
    { name: 'High', key: 'high', value: bands.high },
    { name: 'Medium', key: 'medium', value: bands.medium },
    { name: 'Low', key: 'low', value: bands.low },
    { name: 'No match', key: 'none', value: bands.none },
  ];
  const approvalRate = d.mappings.total
    ? (d.mappings.approved / d.mappings.total) * 100
    : 0;

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard' }]} />
      <div className="page-head">
        <div>
          <h1>Mapping overview</h1>
          <div className="page-sub">
            Current state of the GIIB master, the insurer masters loaded against it, and the
            review work outstanding.
          </div>
        </div>
      </div>

      <div className="tiles">
        <Tile label="GIIB vehicles" value={fmt(d.vehicles.total)} foot={`${fmt(d.vehicles.active)} active`} tone="accent" />
        <Tile label="Insurers onboarded" value={fmt(d.insurers.active)} foot={`${fmt(d.insurers.total)} registered`} />
        <Tile label="Mapped links" value={fmt(d.mappings.approved)} foot={`${approvalRate.toFixed(1)}% of current links`} tone="ok" />
        <Tile label="Awaiting review" value={fmt(d.mappings.pending)} foot="Pending analyst decision" tone="warn" />
        <Tile
          label="Band check failures"
          value={fmt(d.mappings.rating.fail)}
          foot="Premium-impacting, cannot auto-approve"
          tone={d.mappings.rating.fail > 0 ? 'danger' : ''}
        />
        <Tile
          label="Average confidence"
          value={d.mappings.avgConfidence === null ? '-' : `${d.mappings.avgConfidence}%`}
          foot={`${fmt(d.mappings.autoApproved)} auto-approved`}
        />
      </div>

      <div className="cols cols-2">
        <Card
          title="Confidence distribution"
          subtitle="Current links grouped by the band the engine assigned"
          actions={<Link className="btn btn-sm" to="/mappings">Open review queue</Link>}
        >
          {d.mappings.total === 0 ? (
            <Empty title="Nothing matched yet">
              Upload an insurer master to run the matching engine.
            </Empty>
          ) : (
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bandData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--text-muted)' }} stroke="var(--border)" />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--text-muted)' }} stroke="var(--border)" width={48} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 6, fontSize: 12,
                    }}
                    formatter={(v) => [fmt(v), 'Records']}
                  />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                    {bandData.map((entry) => <Cell key={entry.key} fill={BAND_COLOR[entry.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Recent imports" subtitle="Latest master files processed" actions={<Link className="btn btn-sm" to="/imports">All imports</Link>}>
          {d.recentBatches.length === 0 ? (
            <Empty title="No imports yet"><Link to="/upload">Upload the first master</Link></Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Insurer</th>
                    <th>Status</th>
                    <th className="num">Rows</th>
                    <th className="num">Auto</th>
                    <th className="num">Review</th>
                    <th>Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {d.recentBatches.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <div className="cell-main">{b.filename}</div>
                        <div className="cell-sub">Batch #{b.id}</div>
                      </td>
                      <td>{b.insurerCode || 'GIIB master'}</td>
                      <td><Badge kind={b.status === 'completed' ? 'approved' : b.status === 'failed' ? 'rejected' : 'pending'}>{b.status}</Badge></td>
                      <td className="num">{fmt(b.totalRows)}</td>
                      <td className="num">{fmt(b.autoMatched)}</td>
                      <td className="num">{fmt(b.needsReview)}</td>
                      <td>{fmtDate(b.finishedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title="Insurer coverage" subtitle="How much of each insurer's master is linked to a universal key" bodyClass="tight">
          {coverage.isLoading ? <div style={{ padding: 16 }}><Loading rows={4} /></div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Insurer</th>
                    <th className="num">Records</th>
                    <th className="num">Approved</th>
                    <th className="num">Pending</th>
                    <th className="num">Band failures</th>
                    <th style={{ minWidth: 160 }}>Coverage</th>
                    <th>Last import</th>
                  </tr>
                </thead>
                <tbody>
                  {(coverage.data || []).map((c) => (
                    <tr key={c.insurerId}>
                      <td>
                        <div className="cell-main">{c.insurerName}</div>
                        <div className="cell-sub">{c.insurerCode}</div>
                      </td>
                      <td className="num">{fmt(c.records)}</td>
                      <td className="num">{fmt(c.approved)}</td>
                      <td className="num">{fmt(c.pending)}</td>
                      <td className="num">
                        {c.ratingFail > 0 ? <Badge kind="fail">{fmt(c.ratingFail)}</Badge> : '-'}
                      </td>
                      <td>
                        <ConfidenceMeter
                          value={c.coveragePct}
                          band={c.coveragePct >= 90 ? 'high' : c.coveragePct >= 60 ? 'medium' : 'low'}
                        />
                      </td>
                      <td>{fmtDate(c.lastImportAt)}</td>
                    </tr>
                  ))}
                  {(coverage.data || []).length === 0 && (
                    <tr><td colSpan={7}><Empty title="No insurer data yet" /></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
